import { Stack } from "expo-router";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  Dimensions,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import "../Localization/i18next";
import "./global.css";
import * as Sentry from "@sentry/react-native";
import Colors from "../Resources/Color";
import {
  AntDesign,
  Entypo,
  Feather,
  FontAwesome,
  FontAwesome5,
  FontAwesome6,
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  Octicons,
  SimpleLineIcons,
} from "@expo/vector-icons";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./auth/AuthContext";
import { AcquisitionProvider } from "./auth/AcquisitionContext";
import { useLanguageFromURL } from "./hooks/useLanguageFromURL";
import { useHtmlLang } from "./hooks/useHtmlLang";
import { ToastProvider } from "./contexts/ToastContext";
import ToastContainer from "./Components/Toast/ToastContainer";
import IconEmptyView from "./Components/IconEmptyView";
import { getAppVersion } from "./utils/version";

class GlobalCrashBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorReport: null };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      console.error("[GlobalCrashBoundary] React error caught", error);
      console.error(
        "[GlobalCrashBoundary] Component stack",
        info?.componentStack
      );

      const report = {
        message: String(error?.message || error),
        name: error?.name,
        stack: error?.stack,
        componentStack: info?.componentStack,
        time: Date.now(),
        lastPointer:
          typeof window !== "undefined"
            ? window.__EfortLastPointerEvent || null
            : null,
      };

      try {
        this.setState({ errorReport: report });
      } catch (_e) {
        // noop
      }

      if (typeof window !== "undefined") {
        window.__EfortLastGlobalReactError = report;
      }
    } catch (_e) {
      // noop
    }
  }

  render() {
    if (this.state.hasError) {
      const reloadPage = () => {
        try {
          if (typeof window !== "undefined" && window.location) {
            window.location.reload();
          }
        } catch (_e) {
          // noop
        }
      };

      return (
        <IconEmptyView
          iconLibrary="Ionicons"
          iconName="alert-circle-outline"
          mainText="Something went wrong"
          subText={
            "Refresh the page if the issue persists contact support @efortapp via instagram"
          }
          marginTop={0}
          marginBottom={0}
          ctaText="Reload page"
          onCtaPress={reloadPage}
        />
      );
    }

    return this.props.children;
  }
}

function GlobalCrashDiagnostics() {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Keep overhead low: capture only warnings/errors.
    const CAPTURE_LEVELS = ["warn", "error"];

    // Limits to prevent log-capture from becoming a CPU/memory sink.
    const MAX_BUFFER_LINES = 250;
    const MAX_LINE_CHARS = 8000;
    const MAX_ARG_CHARS = 2000;
    const MAX_STRING_CHARS = 2000;
    const MAX_DEPTH = 3;
    const MAX_KEYS = 40;
    const MAX_ARRAY = 40;

    const truncate = (text, maxChars) => {
      try {
        if (typeof text !== "string") return String(text);
        if (text.length <= maxChars) return text;
        return `${text.slice(0, maxChars)}… [truncated ${
          text.length - maxChars
        } chars]`;
      } catch (_e) {
        return "[Unserializable]";
      }
    };

    const describeValue = (value) => {
      try {
        if (value == null) return String(value);
        if (Array.isArray(value)) return `Array(${value.length})`;
        if (value instanceof Date) return `Date(${value.toISOString()})`;
        if (value instanceof Error) return value.name || "Error";
        if (typeof value === "object") {
          const ctor = value?.constructor?.name;
          return ctor ? `Object(${ctor})` : "Object";
        }
        return typeof value;
      } catch (_e) {
        return "Object";
      }
    };

    const pruneForJson = (value, depth, seen) => {
      try {
        if (value == null) return value;

        if (typeof value === "string") {
          return truncate(value, MAX_STRING_CHARS);
        }

        if (typeof value === "number" || typeof value === "boolean") {
          return value;
        }

        if (typeof value === "bigint") {
          return `${String(value)}n`;
        }

        if (value instanceof Error) {
          return {
            name: value.name,
            message: truncate(String(value.message || ""), MAX_STRING_CHARS),
            stack: value.stack
              ? truncate(String(value.stack), MAX_LINE_CHARS)
              : undefined,
          };
        }

        if (typeof value === "function") {
          return "[Function]";
        }

        if (typeof value !== "object") {
          return truncate(String(value), MAX_STRING_CHARS);
        }

        // Avoid walking very large reactive graphs.
        if (value && value.$mobx) {
          return `[MobX ${describeValue(value)}]`;
        }

        if (!seen) seen = new WeakSet();
        if (seen.has(value)) return "[Circular]";
        seen.add(value);

        if (depth <= 0) {
          return `[Truncated ${describeValue(value)}]`;
        }

        if (Array.isArray(value)) {
          const clipped = value
            .slice(0, MAX_ARRAY)
            .map((v) => pruneForJson(v, depth - 1, seen));
          if (value.length > MAX_ARRAY) {
            clipped.push(`[+${value.length - MAX_ARRAY} more items]`);
          }
          return clipped;
        }

        const result = {};
        let keys = [];
        try {
          keys = Object.keys(value);
        } catch (_e) {
          return `[Unenumerable ${describeValue(value)}]`;
        }

        const clippedKeys = keys.slice(0, MAX_KEYS);
        clippedKeys.forEach((k) => {
          try {
            result[k] = pruneForJson(value[k], depth - 1, seen);
          } catch (_e) {
            result[k] = "[Unserializable]";
          }
        });
        if (keys.length > MAX_KEYS) {
          result.__truncated_keys__ = keys.length - MAX_KEYS;
        }
        return result;
      } catch (_e) {
        return "[Unserializable]";
      }
    };

    // Stamp build sessions to make logs easier to correlate across reloads.
    window.__EfortCrashDiagSessionId =
      window.__EfortCrashDiagSessionId ||
      `diag-${Math.random().toString(36).slice(2, 10)}`;

    const safeStringify = (value) => {
      try {
        const pruned = pruneForJson(value, MAX_DEPTH, new WeakSet());
        return truncate(JSON.stringify(pruned, null, 2), MAX_LINE_CHARS);
      } catch (_e) {
        try {
          return truncate(String(value), MAX_LINE_CHARS);
        } catch (_e2) {
          return "[Unserializable]";
        }
      }
    };

    const formatConsoleArg = (arg) => {
      try {
        if (arg instanceof Error) {
          const name = arg.name || "Error";
          const msg = arg.message || "";
          const stack = arg.stack ? `\n${arg.stack}` : "";
          return truncate(`${name}: ${msg}${stack}`, MAX_LINE_CHARS);
        }
      } catch (_e) {
        // ignore
      }
      if (typeof arg === "string") return arg;
      if (typeof arg === "number" || typeof arg === "boolean")
        return String(arg);
      if (arg == null) return String(arg);
      if (typeof arg === "object")
        return truncate(safeStringify(arg), MAX_ARG_CHARS);
      return truncate(String(arg), MAX_ARG_CHARS);
    };

    const installConsoleCapture = () => {
      try {
        if (window.__EfortConsolePatched) return;
        window.__EfortConsolePatched = true;

        window.__EfortConsoleBuffer = Array.isArray(window.__EfortConsoleBuffer)
          ? window.__EfortConsoleBuffer
          : [];

        const pushLine = (level, args) => {
          try {
            if (!CAPTURE_LEVELS.includes(level)) return;
            const prefix = `[${new Date().toISOString()}] [${level}]`;
            const parts = Array.from(args).map(formatConsoleArg);
            const text = truncate(
              `${prefix} ${parts.join(" ")}`,
              MAX_LINE_CHARS
            );

            window.__EfortConsoleBuffer.push({
              time: Date.now(),
              level,
              text,
            });

            if (window.__EfortConsoleBuffer.length > MAX_BUFFER_LINES) {
              window.__EfortConsoleBuffer.splice(
                0,
                window.__EfortConsoleBuffer.length - MAX_BUFFER_LINES
              );
            }
          } catch (_e) {
            // noop
          }
        };

        window.__EfortConsoleOriginal = window.__EfortConsoleOriginal || {
          log: console.log,
          warn: console.warn,
          error: console.error,
          info: console.info,
          debug: console.debug,
        };

        const wrap = (level, original) => {
          return (...args) => {
            pushLine(level, args);
            try {
              return original.apply(console, args);
            } catch (_e) {
              // noop
            }
          };
        };

        console.log = wrap("log", window.__EfortConsoleOriginal.log);
        console.warn = wrap("warn", window.__EfortConsoleOriginal.warn);
        console.error = wrap("error", window.__EfortConsoleOriginal.error);
        console.info = wrap("info", window.__EfortConsoleOriginal.info);
        console.debug = wrap("debug", window.__EfortConsoleOriginal.debug);

        try {
          window.__EfortDiagCaptureLevels = CAPTURE_LEVELS;
        } catch (_e) {
          // noop
        }
      } catch (_e) {
        // noop
      }
    };

    installConsoleCapture();

    const safeDescribeNode = (node) => {
      try {
        if (!node || typeof node !== "object") {
          return null;
        }
        const tagName = node.tagName;
        const id = node.id;
        const className = node.className;
        return { tagName, id, className: String(className || "") };
      } catch (_e) {
        return null;
      }
    };

    const capturePointer = (evt) => {
      try {
        const target = evt?.target;
        const path =
          typeof evt?.composedPath === "function" ? evt.composedPath() : null;
        window.__EfortLastPointerEvent = {
          type: evt?.type,
          time: Date.now(),
          target: safeDescribeNode(target),
          path: Array.isArray(path)
            ? path.slice(0, 6).map((n) => safeDescribeNode(n))
            : null,
        };
      } catch (_e) {
        // noop
      }
    };

    const onWindowError = (event) => {
      try {
        const err = event?.error;
        window.__EfortLastWindowError = {
          message: String(err?.message || event?.message || "window.error"),
          name: err?.name,
          stack: err?.stack,
          filename: event?.filename,
          lineno: event?.lineno,
          colno: event?.colno,
          time: Date.now(),
          lastPointer: window.__EfortLastPointerEvent || null,
        };
        console.error("[window.error]", window.__EfortLastWindowError);
      } catch (_e) {
        // noop
      }
    };

    const onUnhandledRejection = (event) => {
      try {
        const reason = event?.reason;
        window.__EfortLastUnhandledRejection = {
          message: String(reason?.message || reason || "unhandledrejection"),
          name: reason?.name,
          stack: reason?.stack,
          time: Date.now(),
          lastPointer: window.__EfortLastPointerEvent || null,
        };
        console.error(
          "[unhandledrejection]",
          window.__EfortLastUnhandledRejection
        );
      } catch (_e) {
        // noop
      }
    };

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    if (typeof document !== "undefined") {
      document.addEventListener("pointerdown", capturePointer, true);
      document.addEventListener("touchstart", capturePointer, true);
      document.addEventListener("click", capturePointer, true);
    }

    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      if (typeof document !== "undefined") {
        document.removeEventListener("pointerdown", capturePointer, true);
        document.removeEventListener("touchstart", capturePointer, true);
        document.removeEventListener("click", capturePointer, true);
      }
    };
  }, []);

  return null;
}

// Initialize Sentry
const EFORT_APP_VERSION = getAppVersion();

Sentry.init({
  dsn: "https://62de5b4b8ff8bbd6b6227110f2a1e850@o4510051247128577.ingest.de.sentry.io/4510051250274384",

  // Helps correlate issues to the exact deployed build.
  // Convention: <app-name>@<version>
  release: `efortcoach@${EFORT_APP_VERSION}`,
  dist: EFORT_APP_VERSION,

  // Adds more context data to events (IP address, cookies, user, etc.)
  sendDefaultPii: true,

  // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing.
  // We recommend adjusting this value in production.
  // tracesSampleRate: 1.0,

  // Enable logs to be sent to Sentry
  // enableLogs: false,

  // Disable debug mode
  // debug: false,

  // profilesSampleRate is relative to tracesSampleRate.
  // Here, we'll capture profiles for 100% of transactions.
  // profilesSampleRate: 1.0,

  // Record session replays for 100% of errors and 100% of sessions (for testing)
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  integrations: [
    Sentry.mobileReplayIntegration({
      // Disable masking for testing (don't use in production with sensitive data)
      // maskAllText: false,
      // maskAllImages: false,
      // maskAllVectors: false,
    }),
  ],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

// Additional tag for querying/grouping in Sentry.
try {
  Sentry.setTag("app_version", EFORT_APP_VERSION);
} catch (_e) {
  // noop
}

// ScreenDimensions context and hook
const ScreenDimensionsContext = createContext();
export const useScreenDimensions = () => useContext(ScreenDimensionsContext);

function getScreenSize(width) {
  if (width < 600) return "small"; // Phones
  if (width >= 600 && width < 1090) return "medium"; // Tablets
  return "large"; // Desktops
}

export const ScreenDimensionsProvider = ({ children }) => {
  const [screenSize, setScreenSize] = useState(
    getScreenSize(Dimensions.get("window").width)
  );

  useEffect(() => {
    const handleResize = ({ window }) => {
      setScreenSize(getScreenSize(window.width));
    };

    const subscription = Dimensions.addEventListener("change", handleResize);
    return () => subscription.remove();
  }, []);

  return (
    <ScreenDimensionsContext.Provider
      value={{
        screenWidth: 1430,
        screenHeight: 1000,
        ultraWideScreen: false,
        screenSize,
      }}
    >
      {children}
    </ScreenDimensionsContext.Provider>
  );
};

// Device context and hook
const DeviceContext = createContext({ isMobile: false });
export const DeviceProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // phones & small touch-devices
    const mobileRegex =
      /Android.*Mobile|iP(hone|od)|BlackBerry|BB10|IEMobile|Opera Mini|Windows Phone|webOS|Kindle|Silk-Accelerated/i;
    // tablets (including iPadOS 13+, PlayBook, Kindle Fire)
    const tabletRegex = /(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i;

    setIsMobile(mobileRegex.test(ua) || tabletRegex.test(ua));
  }, []);
  return (
    <DeviceContext.Provider value={{ isMobile: isMobile }}>
      {children}
    </DeviceContext.Provider>
  );
};
export const useDevice = () => useContext(DeviceContext);

// Language detector component
function LanguageDetector({ children }) {
  useLanguageFromURL();
  useHtmlLang(); // Update HTML lang attribute when language changes
  return <>{children}</>;
}

// Icon preloader component - preloads all icons used in the app to prevent flash on first render
function IconPreloader() {
  return (
    <View style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}>
      {/* Ionicons */}
      <Ionicons name="barbell-sharp" size={1} />
      <Ionicons name="filter" size={1} />
      <Ionicons name="close" size={1} />
      <Ionicons name="school" size={1} />
      <Ionicons name="settings-outline" size={1} />
      <Ionicons name="chevron-forward" size={1} />
      <Ionicons name="chevron-back" size={1} />
      <Ionicons name="eye" size={1} />
      <Ionicons name="pulse-outline" size={1} />
      <Ionicons name="scale-outline" size={1} />
      <Ionicons name="podium" size={1} />
      <Ionicons name="podium-outline" size={1} />
      <Ionicons name="play-outline" size={1} />
      <Ionicons name="download-outline" size={1} />
      <Ionicons name="cloud-offline-outline" size={1} />
      <Ionicons name="alert-circle-outline" size={1} />
      <Ionicons name="cloud-outline" size={1} />
      <Ionicons name="checkmark" size={1} />
      <Ionicons name="chatbox-outline" size={1} />
      <Ionicons name="card-outline" size={1} />
      <Ionicons name="person-outline" size={1} />
      <Ionicons name="person" size={1} />
      <Ionicons name="reader-outline" size={1} />
      <Ionicons name="trash-outline" size={1} />
      <Ionicons name="videocam" size={1} />
      <Ionicons name="arrow-down-circle" size={1} />

      {/* Feather */}
      <Feather name="menu" size={1} />
      <Feather name="chevron-left" size={1} />
      <Feather name="chevron-down" size={1} />
      <Feather name="chevron-up" size={1} />
      <Feather name="edit-3" size={1} />
      <Feather name="minus" size={1} />
      <Feather name="plus" size={1} />
      <Feather name="copy" size={1} />
      <Feather name="log-out" size={1} />
      <Feather name="minimize-2" size={1} />
      <Feather name="x" size={1} />

      {/* FontAwesome5 */}
      <FontAwesome5 name="dumbbell" size={1} />
      <FontAwesome5 name="google-pay" size={1} />

      {/* Entypo */}
      <Entypo name="dots-three-horizontal" size={1} />

      {/* Octicons */}
      <Octicons name="check" size={1} />
      <Octicons name="search" size={1} />

      {/* MaterialIcons */}
      <MaterialIcons name="check-box" size={1} />
      <MaterialIcons name="check-box-outline-blank" size={1} />
      <MaterialIcons name="check" size={1} />
      <MaterialIcons name="drag-indicator" size={1} />

      {/* MaterialCommunityIcons */}
      <MaterialCommunityIcons name="filter" size={1} />

      {/* AntDesign */}
      <AntDesign name="plus" size={1} />
      <AntDesign name="minus" size={1} />
      <AntDesign name="close" size={1} />

      {/* FontAwesome */}
      <FontAwesome name="copy" size={1} />

      {/* FontAwesome6 */}
      <FontAwesome6 name="file" size={1} />
      <FontAwesome6 name="plus" size={1} />

      {/* SimpleLineIcons */}
      <SimpleLineIcons name="user" size={1} />
    </View>
  );
}

// App layout component
export default Sentry.wrap(function AppLayout() {
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  const Backend = isMobileDevice ? TouchBackend : HTML5Backend;
  // Add a small long-press delay for touch devices to start dragging
  const backendOptions = isMobileDevice
    ? {
        delay: 100, // milliseconds to wait before drag starts on touch
        // touchSlop: 5, // small movement tolerance before starting drag
        // enableMouseEvents: true, // supports hybrid devices nicely
      }
    : undefined;

  const queryClient = new QueryClient();

  return (
    <GlobalCrashBoundary>
      <GlobalCrashDiagnostics />
      <SafeAreaProvider>
        <DndProvider backend={Backend} options={backendOptions}>
          <ScreenDimensionsProvider>
            <DeviceProvider>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <AcquisitionProvider>
                    <ToastProvider>
                      <LanguageDetector>
                        {/* Preload icons to prevent flash on first render */}
                        <IconPreloader />
                        <ToastContainer />
                        <Stack screenOptions={{ headerShown: false }}>
                          <Stack.Screen
                            name="index"
                            options={{
                              headerShown: false,
                              title: "Efort Coach",
                            }}
                          />
                          <Stack.Screen
                            name="login"
                            options={{
                              headerShown: false,
                              title: "Efort Coach - Login",
                            }}
                          />
                          <Stack.Screen
                            name="signup"
                            options={{
                              headerShown: false,
                              title: "Efort Coach - Sign up",
                            }}
                          />
                          <Stack.Screen
                            name="onboarding"
                            options={{ headerShown: false }}
                          />
                          <Stack.Screen
                            name="[coachApplications]/index"
                            options={{ headerShown: false }}
                          />
                        </Stack>
                      </LanguageDetector>
                    </ToastProvider>
                  </AcquisitionProvider>
                </AuthProvider>
              </QueryClientProvider>
            </DeviceProvider>
          </ScreenDimensionsProvider>
        </DndProvider>
      </SafeAreaProvider>
    </GlobalCrashBoundary>
  );
});

/*
 * Optional iOS Safari/WebKit mitigation (DISABLED):
 * Swallows DOM NotFoundError from Node.prototype.removeChild during React commits.
 *
 * This can stop production crashes but may hide underlying DOM churn bugs.
 * Kept here commented-out for reference only.
 */
// function installRemoveChildGuardForIOS() {
//   try {
//     if (typeof window === "undefined") return;
//
//     const isIOS = (() => {
//       try {
//         const ua = navigator.userAgent || "";
//         const platform = navigator.platform || "";
//         const isAppleMobile =
//           /iPad|iPhone|iPod/.test(ua) || /iPad|iPhone|iPod/.test(platform);
//         const isIpadOs =
//           platform === "MacIntel" &&
//           typeof navigator.maxTouchPoints === "number" &&
//           navigator.maxTouchPoints > 1;
//         return isAppleMobile || isIpadOs;
//       } catch (_e) {
//         return false;
//       }
//     })();
//
//     if (!isIOS) return;
//     if (window.__EfortRemoveChildGuardInstalled) return;
//     window.__EfortRemoveChildGuardInstalled = true;
//
//     const proto = window.Node && window.Node.prototype;
//     if (!proto || typeof proto.removeChild !== "function") return;
//
//     const original = proto.removeChild;
//     window.__EfortOriginalRemoveChild = window.__EfortOriginalRemoveChild || original;
//
//     proto.removeChild = function patchedRemoveChild(child) {
//       try {
//         return original.call(this, child);
//       } catch (e) {
//         try {
//           const name = e?.name;
//           const message = String(e?.message || "");
//           const looksLikeWebkitNotFound =
//             name === "NotFoundError" &&
//             (message.includes("can not be found here") ||
//               message.includes("cannot be found") ||
//               message.includes("not found"));
//
//           const alreadyDetached =
//             !!child &&
//             typeof child === "object" &&
//             child.parentNode &&
//             child.parentNode !== this;
//
//           if (looksLikeWebkitNotFound || alreadyDetached) {
//             const parentDesc = (() => {
//               try {
//                 return {
//                   tagName: this?.tagName,
//                   id: this?.id,
//                   className: String(this?.className || ""),
//                 };
//               } catch (_e2) {
//                 return null;
//               }
//             })();
//
//             const childDesc = (() => {
//               try {
//                 return {
//                   tagName: child?.tagName,
//                   id: child?.id,
//                   className: String(child?.className || ""),
//                 };
//               } catch (_e2) {
//                 return null;
//               }
//             })();
//
//             const stack = (() => {
//               try {
//                 throw new Error("removeChild NotFoundError intercepted");
//               } catch (err) {
//                 return err?.stack;
//               }
//             })();
//
//             window.__EfortLastDomNotFoundError = {
//               time: Date.now(),
//               name,
//               message,
//               parent: parentDesc,
//               child: childDesc,
//               lastPointer: window.__EfortLastPointerEvent || null,
//               stack,
//             };
//
//             console.error(
//               "[DOMGuard] Swallowed removeChild NotFoundError",
//               window.__EfortLastDomNotFoundError
//             );
//
//             return child;
//           }
//         } catch (_inner) {
//           // ignore and rethrow original
//         }
//         throw e;
//       }
//     };
//   } catch (_e) {
//     // noop
//   }
// }
